import React, { useEffect, useMemo, useState } from "react";
import {
  FaFileExcel,
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
} from "react-icons/fa";
import Select from "react-select";
import { axiosMaster } from "../api/axiosInstance";
import AddEditUserMaster from "./AddEditUserMaster";

const UserMaster = () => {
  /* ================= STATE ================= */
  const [groupList, setGroupList] = useState([]);
  const [restaurantList, setRestaurantList] = useState([]);
  const [userList, setUserList] = useState([]);

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  /* ================= LOAD GROUPS ================= */
  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const res = await axiosMaster.get("/GroupMaster");
      setGroupList(
        (res.data || []).map((g) => ({
          value: g.groupId,
          label: g.groupName,
        }))
      );
    } catch (err) {
      console.error(err);
    }
  };

  /* ================= FETCH USERS ================= */
  const fetchUsers = async (restId) => {
    if (restId === undefined || restId === null) return;

    try {
      setLoading(true);

      const res = await axiosMaster.get(
        `/UserMaster/GetUserMasterByRestaurantId?restId=${restId}`
      );

      setUserList(res.data || []);
    } catch (err) {
      console.error("User fetch error", err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= GROUP CHANGE ================= */
  const handleGroupChange = async (option) => {
    setSelectedGroup(option);
    setSelectedRestaurant(null);
    setRestaurantList([]);
    setUserList([]);

    if (!option) return;

    try {
      const res = await axiosMaster.get(
        `/RestaurantMaster/GetByGroup?groupId=${option.value}`
      );

      setRestaurantList(
        (res.data || []).map((r) => ({
          value: r.restId,
          label: r.restaurantName,
        }))
      );
    } catch (err) {
      console.error(err);
    }
  };

  /* ================= REST CHANGE ================= */
  const handleRestaurantChange = (option) => {
    setSelectedRestaurant(option);
    setUserList([]);

    if (!option) return;
    fetchUsers(option.value);
  };

  /* ================= SEARCH ================= */
  const filteredData = useMemo(() => {
    const term = search.toLowerCase();
    return userList.filter((u) =>
      (u.user_Name || "").toLowerCase().includes(term)
    );
  }, [userList, search]);

  /* ================= ACTIONS ================= */
  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const handleEdit = (row) => {
    setEditing(row);
    setFormOpen(true);
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Delete this user?")) return;

    try {
      await axiosMaster.delete(`/UserMaster/${row.user_Id}`);
      fetchUsers(selectedRestaurant?.value);
    } catch (err) {
      console.error("Delete error", err);
    }
  };

  /* ================= EXCEL ================= */
  const exportExcel = () => {
    if (!filteredData.length) return;

    const rows = filteredData.map((r) => ({
      UserId: r.user_Id,
      UserName: `"${r.user_Name}"`,
      Status: r.status ? "Active" : "Inactive",
      EntryDate: r.entryDate,
    }));

    const csv =
      "data:text/csv;charset=utf-8," +
      [
        Object.keys(rows[0]).join(","),
        ...rows.map((e) => Object.values(e).join(",")),
      ].join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = "UserMaster.csv";
    link.click();
  };

  const isAddEnabled =
    selectedGroup?.value !== undefined &&
    selectedRestaurant?.value !== undefined;

  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  };

  /* ================= UI ================= */
  return (
    <div className="bg-white rounded-xl shadow-lg p-5 border">
      {/* HEADER */}
      <div className="flex flex-wrap gap-2 mb-4 items-center justify-between">
        <h2 className="text-xl font-bold">User Master</h2>

        <div className="flex flex-wrap gap-2">
          <Select
            options={groupList}
            value={selectedGroup}
            onChange={handleGroupChange}
            placeholder="Select group..."
            styles={selectStyles}
            menuPortalTarget={document.body}
            isClearable
          />

          <Select
            options={restaurantList}
            value={selectedRestaurant}
            onChange={handleRestaurantChange}
            placeholder="Select restaurant..."
            styles={selectStyles}
            menuPortalTarget={document.body}
            isClearable
          />

          <div className="relative">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              className="pl-9 pr-3 py-2 border rounded-lg"
              placeholder="Search user..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <button
            onClick={exportExcel}
            className="px-3 py-2 bg-green-600 text-white rounded-lg"
          >
            <FaFileExcel />
          </button>

          <button
            onClick={openAdd}
            disabled={!isAddEnabled}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2"
          >
            <FaPlus /> Add User
          </button>
        </div>
      </div>

      {/* TABLE */}
      <table className="w-full text-sm border">
        <thead className="bg-gray-100">
          <tr>
            <th className="p-2 border">User ID</th>
            <th className="p-2 border">User Name</th>
            <th className="p-2 border">Status</th>
            <th className="p-2 border">Action</th>
          </tr>
        </thead>

        <tbody>
          {filteredData.map((row) => (
            <tr key={row.user_Id}>
              <td className="p-2 border">{row.user_Id}</td>
              <td className="p-2 border">{row.user_Name}</td>
              <td className="p-2 border">
                {row.status ? "Active" : "Inactive"}
              </td>
              <td className="p-2 border flex gap-2">
                <FaEdit
                  className="text-blue-600 cursor-pointer"
                  onClick={() => handleEdit(row)}
                />
                <FaTrash
                  className="text-red-600 cursor-pointer"
                  onClick={() => handleDelete(row)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* MODAL */}
      {formOpen && (
        <AddEditUserMaster
          editData={editing}
          groupId={selectedGroup?.value}
          restId={selectedRestaurant?.value}
          onClose={() => setFormOpen(false)}
          onSuccess={() => fetchUsers(selectedRestaurant?.value)}
        />
      )}
    </div>
  );
};

export default UserMaster;